import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { isPublicRoute } from './routesPubliques'; // 🚨 jamais d'éjection d'un client sur une route publique
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '@/lib/apiUrl';
import { reportClientEvent } from '@/lib/clientDiagnostics';
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples

const AuthContext = createContext(null);

// 🔐 Connexion P2 : la coupure quotidienne à 3 h (`nextThreeAM` / `session_expires_at`) est supprimée — la session suit `exp` du JWT
// (durée configurée côté Sécurité, renouvelée à chaque utilisation).
const RENOUVELLEMENT_APRES_MS = 60 * 60 * 1000; // 1 h : âge minimal du token avant renouvellement (throttle)

/** Décode le payload d'un JWT sans le vérifier (affichage/décision client seulement). */
export function decoderJwt(token) {
  try { return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))); } catch { return null; }
}

/** Token expiré selon `exp` (secondes) ? */
export function tokenExpire(token, maintenantMs = Date.now()) {
  const p = decoderJwt(token);
  return !!(p && p.exp && p.exp * 1000 <= maintenantMs);
}

/** Token âgé de plus de 1 h (d'après `iat`) ? */
export function tokenARenouveler(token, maintenantMs = Date.now()) {
  const p = decoderJwt(token);
  return !!(p && p.iat && maintenantMs - p.iat * 1000 >= RENOUVELLEMENT_APRES_MS && !p.kiosk && !p.is_impersonation);
}

/** Récupère le token depuis localStorage ou sessionStorage */
function getStoredToken() {
  return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
}

/** Récupère les infos d'impersonation depuis le storage (survit aux rechargements) */
function getStoredImpersonationInfo() {
  const stored = localStorage.getItem('impersonation_info') || sessionStorage.getItem('impersonation_info');
  try { return stored ? JSON.parse(stored) : null; } catch { return null; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getStoredToken);
  const [loading, setLoading] = useState(true);
  const [impersonationInfo, setImpersonationInfo] = useState(getStoredImpersonationInfo);

  // 🆕 [2A.3] Détection : user passe de non-null à null sans logout explicite
  const prevUserRef = useRef(null);
  const userBecameNullReasonRef = useRef(null);
  useEffect(() => {
    const prev = prevUserRef.current;
    if (prev && !user) {
      const reason = userBecameNullReasonRef.current || 'unknown';
      try {
        reportClientEvent('user_became_null', 'critical', {
          previous_role: prev?.role || '',
          previous_email: prev?.email || '',
          reason,
        });
      } catch { /* silencieux */ }
    }
    prevUserRef.current = user;
    // Reset le reason après chaque transition
    if (!user) userBecameNullReasonRef.current = null;
  }, [user]);

  /** Session expirée selon `exp` du JWT (plus aucune coupure arbitraire à 3 h). */
  const checkSessionExpiry = useCallback(() => {
    const t = getStoredToken();
    if (t && tokenExpire(t)) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('session_expires_at');
      sessionStorage.removeItem('auth_token');
      userBecameNullReasonRef.current = 'session_expired';
      setToken(null);
      setUser(null);
      return true;
    }
    return false;
  }, []);

  /** 🔐 Connexion P2 — renouvellement glissant : token > 1 h → `POST /api/auth/renouveler`, remplacé dans le MÊME stockage. */
  const dernierRenouvellementRef = useRef(0);
  const renouvelerSiNecessaire = useCallback(async () => {
    const t = getStoredToken();
    if (!t || !tokenARenouveler(t) || Date.now() - dernierRenouvellementRef.current < RENOUVELLEMENT_APRES_MS) return false;
    dernierRenouvellementRef.current = Date.now();
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/renouveler`, {}, { headers: { Authorization: `Bearer ${t}` } });
      if (data?.renouvele && data.token) {
        if (localStorage.getItem('auth_token')) localStorage.setItem('auth_token', data.token);
        else sessionStorage.setItem('auth_token', data.token);
        setToken(data.token);
        return true;
      }
    } catch { /* non bloquant : la session reste valide jusqu'à `exp` */ }
    return false;
  }, []);

  useEffect(() => {
    if (checkSessionExpiry()) {
      setLoading(false);
      return;
    }
    if (token) {
      verifyToken();
      renouvelerSiNecessaire();
    } else {
      setLoading(false);
    }
    const interval = setInterval(checkSessionExpiry, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // 🆕 [2B.2] Détecte le retour d'arrière-plan (Safari iOS notamment).
  // Si l'onglet redevient visible après >5 min d'inactivité ET qu'on a un token,
  // re-déclencher verifyToken() pour rafraîchir le user au cas où Safari aurait
  // purgé les caches mémoire ou que les permissions auraient changé.
  const lastHiddenAtRef = useRef(null);
  useEffect(() => {
    const onVisibilityChange = () => {
      try {
        if (document.visibilityState === 'hidden') {
          lastHiddenAtRef.current = Date.now();
          return;
        }
        if (document.visibilityState === 'visible') {
          const hiddenAt = lastHiddenAtRef.current;
          lastHiddenAtRef.current = null;
          if (!hiddenAt) return;
          const inactiveMs = Date.now() - hiddenAt;
          if (inactiveMs < 5 * 60 * 1000) return; // moins de 5 min : pas nécessaire
          // Token toujours dispo en local ?
          const t = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
          if (!t) return;
          if (checkSessionExpiry()) return;
          // Re-vérifie le token. On ne stocke pas la promise — fire-and-forget.
          verifyToken();
          renouvelerSiNecessaire();
        }
      } catch { /* silencieux */ }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyToken = async () => {
    // Retry robuste — le serveur peut prendre du temps après un déploiement
    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        const { data } = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(data);
        setLoading(false);
        return;
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          // Token invalide/expiré → déconnecter
          userBecameNullReasonRef.current = 'verifyToken_unauthorized';
          // 🚨 Route publique (client sur /facture, /soumission, /presentation…) : nettoyer le jeton SANS rediriger ni alerter
          if (typeof window !== 'undefined' && isPublicRoute(window.location.pathname)) {
            logout();
            setLoading(false);
            return;
          }
          // 🆕 [2B.2] Toast clair + redirect explicite plutôt que disparition silencieuse
          try { toast.error('Session expirée, veuillez vous reconnecter.', { duration: 5000 }); } catch { /* */ }
          logout();
          setLoading(false);
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
          return;
        }
        // Erreur réseau (serveur pas prêt) → réessayer
        if (attempt < 4) {
          await new Promise(r => setTimeout(r, 2000 + attempt * 1000));
        } else {
          // 🆕 [2B.2] Après 5 tentatives, on ne reste PAS en zombie state (token présent
          // mais user=null). On affiche un toast et on rend la main au router : si
          // l'utilisateur n'a jamais été chargé (boot initial), le ProtectedRoute le
          // redirigera vers /login. Sinon (refresh côté session active), on garde le
          // user précédent intact si possible — sinon redirect login.
          setLoading(false);
          if (!user) {
            try { toast.error("Impossible de vérifier votre session. Reconnectez-vous.", { duration: 6000 }); } catch { /* */ }
            // Purge le token pour éviter un loop infini de retry sur les fetchs suivants
            try {
              localStorage.removeItem('auth_token');
              localStorage.removeItem('session_expires_at');
              sessionStorage.removeItem('auth_token');
            } catch { /* */ }
            userBecameNullReasonRef.current = 'verifyToken_network_exhausted';
            setToken(null);
            if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
              window.location.href = '/login';
            }
          }
          return;
        }
      }
    }
  };

  const login = async (email, password, rememberMe = false) => {
    // Retry robuste pour gérer les déploiements
    let lastError;
    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
        if (rememberMe) {
          localStorage.setItem('auth_token', data.token);
          localStorage.removeItem('session_expires_at');
          sessionStorage.removeItem('auth_token');
        } else {
          sessionStorage.setItem('auth_token', data.token);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('session_expires_at');
        }
        setToken(data.token);
        setUser(data.user);
        return data;
      } catch (e) {
        lastError = e;
        const status = e?.response?.status;
        // Si c'est une vraie erreur d'auth (401, 400), ne pas retry
        if (status && status < 500) {
          throw new Error(e?.response?.data?.detail || 'Erreur de connexion');
        }
        // Erreur réseau ou 500+ → retry
        if (attempt < 4) {
          await new Promise(r => setTimeout(r, 2000 + attempt * 1000));
        } else {
          throw new Error('Le serveur est temporairement indisponible. Veuillez patienter quelques instants et reessayer.');
        }
      }
    }
  };

  const updateToken = (newToken) => {
    if (localStorage.getItem('auth_token')) {
      localStorage.setItem('auth_token', newToken);
    } else {
      sessionStorage.setItem('auth_token', newToken);
    }
    setToken(newToken);
    try {
      const payload = JSON.parse(atob(newToken.split('.')[1]));
      setUser(prev => ({ ...prev, must_change_password: payload.must_change_password || false }));
    } catch (e) { console.error("Error:", e); }
  };

  const refreshUser = async () => {
    try {
      const currentToken = getStoredToken();
      if (!currentToken) return;
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      setUser(data);
    } catch (e) { console.error("Error:", e); }
  };

  const logout = () => {
    // 🔐 Connexion P4 : révoque la session/appareil côté serveur (non bloquant), puis purge locale.
    const tokenCourant = localStorage.getItem('auth_token');
    if (tokenCourant) axios.post(`${API_URL}/api/auth/logout`, {}, { headers: { Authorization: `Bearer ${tokenCourant}` } }).catch(() => {});
    // 🚨 [Phase 1.b Hotfix Sécurité — 2026-07-18] Purge complète au logout.
    // Défense-en-profondeur : évite tout leak inter-utilisateur (permissions
    // cached, notifications, préférences UI) sur un poste partagé lorsqu'un
    // employé se déconnecte et qu'un autre se connecte immédiatement après.
    // Préserve UNIQUEMENT les clés fonctionnellement critiques hors session.
    const PRESERVED_KEYS = new Set([
      'kiosk_mode',   // Mode Terminal Entrepôt persistant
      'theme',        // Préférence dark/light — non-sensible
    ]);
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(k => { if (!PRESERVED_KEYS.has(k)) localStorage.removeItem(k); });
    } catch { /* */ }
    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach(k => { if (!PRESERVED_KEYS.has(k)) sessionStorage.removeItem(k); });
    } catch { /* */ }
    setToken(null);
    setUser(null);
  };

  // ─── IMPERSONATION ───
  const impersonate = async (employeeId) => {
    try {
      const currentToken = getStoredToken();
      if (!currentToken) throw new Error("Non authentifié");

      // Décoder le token admin pour récupérer son employee_id (pour le retour)
      let adminEmployeeId = null;
      try { adminEmployeeId = JSON.parse(atob(currentToken.split('.')[1])).employee_id || null; } catch {}

      const { data } = await axios.post(
        `${API_URL}/api/auth/impersonate/${employeeId}`,
        {},
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );

      const impersonationData = {
        ...data.impersonation,
        admin_employee_id: adminEmployeeId,
      };

      // Sauvegarder le token admin
      if (localStorage.getItem('auth_token')) {
        localStorage.setItem('admin_token', currentToken);
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('impersonation_info', JSON.stringify(impersonationData));
        // Sauvegarder la page d'où l'admin a lancé le mode spectateur (pour y retourner au Quitter)
        try { localStorage.setItem('impersonation_return_url', window.location.pathname + window.location.search); } catch {}
      } else {
        sessionStorage.setItem('admin_token', currentToken);
        sessionStorage.setItem('auth_token', data.token);
        sessionStorage.setItem('impersonation_info', JSON.stringify(impersonationData));
        try { sessionStorage.setItem('impersonation_return_url', window.location.pathname + window.location.search); } catch {}
      }

      setToken(data.token);
      setUser(data.user);
      setImpersonationInfo(impersonationData);
    } catch (e) {
      console.error("Erreur impersonation:", e);
      throw new Error(e?.response?.data?.detail || "Erreur lors de la connexion");
    }
  };

  const stopImpersonating = async () => {
    const adminToken = localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
    if (!adminToken) return;

    // Récupérer l'employee_id admin avant de nettoyer
    const storedInfo = getStoredImpersonationInfo();
    const adminEmployeeId = storedInfo?.admin_employee_id || null;

    // Récupérer l'URL de retour sauvegardée (page d'origine avant l'impersonation)
    const returnUrl = localStorage.getItem('impersonation_return_url')
      || sessionStorage.getItem('impersonation_return_url')
      || null;

    // Restaurer le token admin et nettoyer l'impersonation
    if (localStorage.getItem('auth_token')) {
      localStorage.setItem('auth_token', adminToken);
      localStorage.removeItem('admin_token');
      localStorage.removeItem('impersonation_info');
      localStorage.removeItem('impersonation_return_url');
    } else {
      sessionStorage.setItem('auth_token', adminToken);
      sessionStorage.removeItem('admin_token');
      sessionStorage.removeItem('impersonation_info');
      sessionStorage.removeItem('impersonation_return_url');
    }

    setToken(adminToken);
    setImpersonationInfo(null);

    // Appel DIRECT avec adminToken (pas via verifyToken qui lit le state stale)
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setUser(data);
      // Priorité : retour vers la page d'origine (ex: Compte & Accès) ; sinon Mon Espace / Dashboard
      const targetId = adminEmployeeId || data.employee_id;
      const fallback = targetId ? '/mon-espace' : '/dashboard';
      window.location.href = returnUrl || fallback;
    } catch (e) {
      console.error("Erreur restauration admin:", e);
      window.location.href = returnUrl || '/dashboard';
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateToken,
    refreshUser,
    impersonate,
    stopImpersonating,
    impersonationInfo,
    // 🔒 Cat H — Infrastructure : dérivés d'identité exposés par AuthContext (isProprietaire = identité Owner, isChefEquipe/isInstallateur = rôle terrain).
    //   Ces flags ne sont PAS des gates RBAC : ils servent au dispatch UI et sont consommés par des composants qui appliquent en aval `canDo(...)` / `canSeePage(...)` du PermissionsContext.
    //   Voir CATALOGUE_ACTIONS_PAR_PAGE_V13_61.md §13.3.
    isProprietaire: estProprietaire(user),
    isChefEquipe: aLeRole(user, 'chef_equipe') || user?.role_terrain === 'chef_equipe',
    isAdmin: ['proprietaire', 'adjoint_admin'].includes(user?.role),
    isInstallateur: aLeRole(user, 'installateur'),
    permissions: user?.permissions || {},
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit etre utilise dans un AuthProvider');
  return ctx;
}
